import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react";
import Toolbar, { Item } from "devextreme-react/toolbar";
import {
    DataGrid,
    Column,
    Editing,
    Scrolling,
    FilterRow,
    HeaderFilter,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { LoadPanel } from "devextreme-react/load-panel";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/* Dynamic-form layout key for the Layout tab header form (distinct from the ABLAYOUT serial layout). */
const IMPORT_LAYOUT_FORM_ID = "9B2C1A73-5E64-4F82-A1D9-3C7E8F0A2B46";

const yesNoItems = [
    { ID: "Y", Text: "Yes" },
    { ID: "N", Text: "No" },
];
const dateFormatItems = ["MMDDYYYY", "DDMMYYYY", "YYYYMMDD", "MMDDYY", "DDMMYY"].map((v) => ({ ID: v, Text: v }));
const delimiterItems = [
    { ID: "Comma", Text: "Comma" },
    { ID: "Tab", Text: "Tab" },
    { ID: "Semicolon", Text: "Semicolon" },
    { ID: "Pipe", Text: "Pipe" },
    { ID: "Space", Text: "Space" },
    { ID: "Custom", Text: "Custom" },
];

const emptyLayoutForm = {
    LayoutName: "",
    DefaultFlag: "N",
    ReservedLines: 0,
    DateFormat: "MMDDYYYY",
    RecordDelimited: "Y",
    DelimiterCode: "Comma",
    Delimiter: ",",
    Comment: "",
};

/* Reads the PascalCase ImportSerialNumbersResponse (also tolerates camelCase) into flat arrays. */
const readResponse = (data) => {
    const root = data && typeof data === "object" && data.value != null && typeof data.value === "object" ? data.value : (data || {});
    const pick = (a, b) => (root[a] !== undefined && root[a] !== null ? root[a] : root[b]);
    const toLines = (v) => {
        if (v == null) return [];
        if (Array.isArray(v)) return v.map((x) => (x != null ? String(x) : ""));
        return [String(v)];
    };
    return {
        success: pick("success", "Success") !== false,
        linesImported: Number(pick("linesImported", "LinesImported")) || 0,
        importAllowed: Boolean(pick("importAllowed", "ImportAllowed")),
        messages: toLines(pick("messages", "Messages")),
        warnings: toLines(pick("warnings", "Warnings")),
        errors: toLines(pick("errors", "Errors")),
    };
};

/* Maps a full layout DTO (PascalCase from the API) to the header form fields. */
const dtoToForm = (dto) => ({
    LayoutName: dto?.LayoutName ?? "",
    DefaultFlag: dto?.DefaultFlag ? "Y" : "N",
    ReservedLines: dto?.ReservedLines ?? 0,
    DateFormat: dto?.DateFormat ?? "MMDDYYYY",
    RecordDelimited: dto?.RecordDelimited === false ? "N" : "Y",
    DelimiterCode: dto?.DelimiterCode ?? "Comma",
    Delimiter: dto?.Delimiter ?? ",",
    Comment: dto?.Comment ?? "",
});

const dtoToFields = (dto) =>
    (dto?.Fields ?? []).map((f) => ({
        FieldName: f.FieldName,
        Name: f.Name,
        Start: f.Start ?? 0,
        Length: f.Length ?? 0,
        Required: !!f.Required,
        DefaultValue: f.DefaultValue ?? "",
    }));

const SerialNumberImportPopup = (props) => {
    const { showPopup = {}, setShowPopup, oidc, onSuccess } = props;
    const headerRowId = showPopup.headerRowId;
    const currentUser = oidc?.user;
    const accessToken = currentUser?.access_token;
    const DYNAMICFORMSETTING = currentUser?.profile?.DYNAMICFORMSETTING;
    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    const addFilesInputRef = useRef(null);
    const abortRef = useRef(null);
    const layoutFormRefs = useRef({});
    const fieldsGridRef = useRef(null);
    const layoutFieldsRef = useRef([]);

    const [files, setFiles] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [logLines, setLogLines] = useState([]);
    const [importAllowed, setImportAllowed] = useState(false);
    const [busy, setBusy] = useState(false);

    /* Layout tab state. */
    const [layoutRowId, setLayoutRowId] = useState(showPopup.layoutRowId || null);
    const [layoutForm, setLayoutForm] = useState(emptyLayoutForm);
    const [layoutFields, setLayoutFields] = useState([]);
    const [savingLayout, setSavingLayout] = useState(false);
    const [layoutLoading, setLayoutLoading] = useState(false);
    const [showFind, setShowFind] = useState(false);
    const [layoutList, setLayoutList] = useState([]);

    /* Find grid layout persistence (same pattern as itemDataEntry/List.js). */
    const findGridRef = useRef(null);
    const findGridKey = "DBEE7B59-1238-43E7-9A44-DFCD6EDAAEAA";
    const [currentLayout, setCurrentLayout] = useState("Default");
    const loadState = useCallback(() => { return GridLayout.sendStorageRequest(findGridKey, "GET", accessToken, null, currentLayout); }, [currentLayout]);
    const saveState = useCallback((state) => { GridLayout.sendStorageRequest(findGridKey, "POST", accessToken, state, currentLayout); }, [findGridKey, currentLayout]);


    const applyLayoutDto = useCallback((dto) => {
        setLayoutRowId(dto?.RowId || null);
        setLayoutForm(dtoToForm(dto));
        setLayoutFields(dtoToFields(dto));
    }, []);

    /* Current header/rowid for building the update payload from grid event handlers (avoids stale closures). */
    const layoutFormRef = useRef(layoutForm);
    layoutFormRef.current = layoutForm;
    const layoutRowIdRef = useRef(layoutRowId);
    layoutRowIdRef.current = layoutRowId;

    /* Posts the whole layout (header + fields) to the update API; returns the saved DTO. */
    const persistLayout = useCallback(async (fields) => {
        const form = layoutFormRef.current;
        if (!form.LayoutName || !form.LayoutName.trim()) {
            throw new Error(MESSAGES.SERIAL_IMPORT_LAYOUT_NAME_REQUIRED);
        }
        const payload = {
            RowId: layoutRowIdRef.current || null,
            LayoutName: form.LayoutName,
            DefaultFlag: form.DefaultFlag === "Y",
            ReservedLines: Number(form.ReservedLines) || 0,
            DateFormat: form.DateFormat,
            RecordDelimited: form.RecordDelimited !== "N",
            DelimiterCode: form.DelimiterCode,
            Delimiter: form.Delimiter,
            Comment: form.Comment,
            Fields: (fields || []).map((f) => ({
                FieldName: f.FieldName,
                Name: f.Name,
                Start: Number(f.Start) || 0,
                Length: Number(f.Length) || 0,
                Required: !!f.Required,
                DefaultValue: f.DefaultValue ?? null,
            })),
        };
        const res = await axios.post(
            `${window._env_.REACT_APP_APIS}/${routes.OM.UpdateLayout}`,
            payload,
            { headers: { ...authHeaders, "Content-Type": "application/json" } }
        );
        return res.data || {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    /* Reads the grid's committed rows (falls back to state). */
    const readFieldRows = useCallback(() => {
        const grid = fieldsGridRef.current?.instance();
        try {
            const dataRows = (grid?.getVisibleRows?.() || [])
                .filter((r) => r.rowType === "data")
                .map((r) => r.data);
            if (dataRows.length > 0) return dataRows;
        } catch {
            /* fall back to state */
        }
        return layoutFieldsRef.current || [];
    }, []);

    /* Editable-grid update pattern: a cell change persists the layout via the update API. */
    const onFieldRowUpdated = useCallback(async () => {
        try {
            const dto = await persistLayout(readFieldRows());
            /* First save of a new layout inserts — capture RowId so later edits update in place. */
            if (dto?.RowId) {
                layoutRowIdRef.current = dto.RowId;
                setLayoutRowId(dto.RowId);
            }
            layoutFieldsRef.current = dtoToFields(dto);
            notify({ message: MESSAGES.SERIAL_IMPORT_LAYOUT_SAVED, width: 200 }, "success", 1500);
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        }
    }, [persistLayout, readFieldRows]);

    const loadLayout = useCallback(async (rowId, isNew = false) => {
        setLayoutLoading(true);
        try {
            /* No rowId => backend loads the screen's Default Flag layout (legacy first-open behavior).
               isNew => blank template for the "Add" action. */
            const params = rowId ? { rowId } : {};
            const res = await axios.get(
                `${window._env_.REACT_APP_APIS}/${routes.OM.GetLayout}`,
                { params, headers: authHeaders }
            );
            applyLayoutDto(res.data || {});
        } catch {
            applyLayoutDto(null);
        } finally {
            setLayoutLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, applyLayoutDto, showPopup.layoutRowId]);

    /* Initial layout load (the order's assigned layout, or a new-layout template). */
    useEffect(() => {
        if (!accessToken) return;
        loadLayout(showPopup.layoutRowId || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, showPopup.layoutRowId]);

    const closePopup = useCallback(() => {
        if (abortRef.current) abortRef.current.abort();
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const onAddFiles = (e) => {
        const picked = Array.from(e.target?.files || []);
        e.target.value = "";
        if (picked.length === 0) return;
        setFiles((prev) => [...prev, ...picked]);
        setImportAllowed(false);
    };

    const removeSelectedFile = () => {
        if (selectedIndex < 0) return;
        setFiles((prev) => prev.filter((_, i) => i !== selectedIndex));
        setSelectedIndex(-1);
        setImportAllowed(false);
    };

    const clearFileList = () => {
        setFiles([]);
        setSelectedIndex(-1);
        setImportAllowed(false);
    };

    const clearLog = () => {
        setLogLines([]);
        setImportAllowed(false);
    };

    /* Runs Preview (dry-run) or Import (commit) across every listed file, using the selected layout. */
    const runImport = useCallback(async (previewOnly) => {
        if (!headerRowId || headerRowId === EMPTY_GUID) {
            alert(MESSAGES.PLEASE_SELECT_TRANSACTION, MESSAGES.ERROR);
            return;
        }
        if (files.length === 0) {
            setLogLines([MESSAGES.SERIAL_IMPORT_CHOOSE_DATA_FILES]);
            return;
        }

        abortRef.current = new AbortController();
        setBusy(true);
        const lines = [];
        lines.push(`${previewOnly ? "Starting Preview" : "Starting Import"} - ${new Date().toLocaleString()}`);
        let anyErrors = false;
        let allImportAllowed = true;
        let totalImported = 0;

        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);
                const params = { headerRowId, preview: previewOnly };
                if (layoutRowId) params.layoutRowId = layoutRowId;
                const res = await axios.post(
                    `${window._env_.REACT_APP_APIS}/${routes.OM.ImportSerialNumbers}`,
                    formData,
                    { params, headers: authHeaders, signal: abortRef.current.signal }
                );
                const r = readResponse(res.data);
                r.messages.forEach((m) => lines.push(m));
                r.warnings.forEach((w) => lines.push(`Warning: ${w}`));
                r.errors.forEach((er) => lines.push(`Error: ${er}`));
                if (r.errors.length > 0 || !r.success) anyErrors = true;
                if (!r.importAllowed) allImportAllowed = false;
                totalImported += r.linesImported;
            }

            lines.push(`${previewOnly ? "Completed Preview" : "Completed Import"} ${anyErrors ? "with ERRORS" : "Successful"} - ${new Date().toLocaleString()}`);
            setLogLines(lines);
            setImportAllowed(previewOnly ? (allImportAllowed && !anyErrors) : false);

            if (!previewOnly && !anyErrors) {
                notify(
                    totalImported > 0
                        ? `Imported ${totalImported} serial number${totalImported === 1 ? "" : "s"}.`
                        : "Import completed.",
                    "success",
                    3000
                );
                if (onSuccess) onSuccess();
            }
        } catch (err) {
            if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
                lines.push("Operation cancelled.");
                setLogLines(lines);
            } else {
                const msg = err.response?.data?.message || err.response?.data || err.message;
                lines.push(`Error: ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
                setLogLines(lines);
            }
        } finally {
            abortRef.current = null;
            setBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, files, headerRowId, layoutRowId, onSuccess]);

    const cancelRun = () => {
        if (abortRef.current) abortRef.current.abort();
    };

    const errorLines = logLines.filter((l) => /^error[:\s]/i.test(l));

    const printLines = (linesToPrint, title) => {
        if (linesToPrint.length === 0) return;
        const w = window.open("", "_blank");
        if (!w) return;
        const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        w.document.write(`<pre style="font-family:Consolas,monospace;font-size:12px;white-space:pre-wrap;">${linesToPrint.map(esc).join("\n")}</pre>`);
        w.document.title = title;
        w.document.close();
        w.focus();
        w.print();
    };

    const saveLog = () => {
        if (logLines.length === 0) return;
        const blob = new Blob([logLines.join("\r\n")], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Import_Log.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    /* ---- Layout tab ---- */
    const buildLayoutSchema = useCallback(
        (form) => [
            {
                groupId: 1,
                fields: [
                    { name: "LayoutName", tableName: "ABLAYOUT", label: "Layout Name", type: "text", required: true, visible: true, maxLength: 100 },
                    { name: "DefaultFlag", tableName: "ABLAYOUT", label: "Default Flag", type: "select", required: false, visible: true },
                    { name: "ReservedLines", tableName: "ABLAYOUT", label: "Reserved Lines", type: "number", required: false, visible: true },
                    { name: "DateFormat", tableName: "ABLAYOUT", label: "Date Format", type: "select", required: false, visible: true },
                ],
            },
            {
                groupId: 2,
                fields: [
                    { name: "RecordDelimited", tableName: "ABLAYOUT", label: "Record Delimited", type: "select", required: false, visible: true },
                    { name: "DelimiterCode", tableName: "ABLAYOUT", label: "Delimiter Char", type: "select", required: false, visible: true },
                    { name: "Delimiter", tableName: "ABLAYOUT", label: "Delimiter", type: "text", required: false, visible: true, readOnly: (form?.DelimiterCode || "Comma") !== "Custom", maxLength: 4 },
                ],
            },
            {
                groupId: 3,
                fields: [
                    { name: "Comment", tableName: "ABLAYOUT", label: "Comment", type: "textarea", required: false, visible: true, maxLength: 500 },
                ],
            }

        ],
        []
    );

    const loadLayoutSelectOptions = useCallback(() => {
        const set = (name, ds) => {
            const ref = layoutFormRefs.current[name];
            const instance = ref?.instance ? ref.instance() : null;
            if (!instance) return;
            instance.option("dataSource", ds);
            instance.option("valueExpr", "ID");
            instance.option("displayExpr", "Text");
        };
        set("DefaultFlag", yesNoItems);
        set("RecordDelimited", yesNoItems);
        set("DateFormat", dateFormatItems);
        set("DelimiterCode", delimiterItems);
    }, []);

    const handleLayoutFormLoad = useCallback(() => {
        setTimeout(() => loadLayoutSelectOptions(), 100);
    }, [loadLayoutSelectOptions]);

    /* Re-apply the select data sources after a layout (re)loads. */
    useEffect(() => {
        setTimeout(() => loadLayoutSelectOptions(), 100);
    }, [layoutRowId, loadLayoutSelectOptions]);

    const newLayout = useCallback(() => {
        loadLayout(null, true);
    }, [loadLayout]);

    const openFind = useCallback(async () => {
        try {
            setLayoutLoading(true);
            const res = await axios.get(
                `${window._env_.REACT_APP_APIS}/${routes.OM.GetLayouts}`,
                { headers: authHeaders }
            );
            setLayoutList(res.data || []);
            setShowFind(true);
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            setLayoutLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    const onFindSelect = useCallback((row) => {
        setShowFind(false);
        if (row?.RowId) loadLayout(row.RowId);
    }, [loadLayout]);

    const saveLayout = useCallback(async () => {
        const grid = fieldsGridRef.current?.instance();
        /* Commit any in-progress cell edit first. */
        if (grid) {
            try { await grid.saveEditData(); } catch { /* ignore */ }
        }
        setSavingLayout(true);
        try {
            const dto = await persistLayout(readFieldRows());
            applyLayoutDto(dto);
            notify({ message: MESSAGES.SERIAL_IMPORT_LAYOUT_SAVED, width: 200 }, "success", 2500);
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            setSavingLayout(false);
        }
    }, [persistLayout, readFieldRows, applyLayoutDto]);

    const deleteLayout = useCallback(async () => {
        if (!layoutRowId) {
            newLayout();
            return;
        }
        const ok = await confirm("Delete selected record?", "Confirm");
        if (!ok) return;
        setSavingLayout(true);
        try {
            await axios.delete(
                `${window._env_.REACT_APP_APIS}/${routes.OM.DeleteLayout}`,
                { params: { rowId: layoutRowId }, headers: authHeaders }
            );
            notify({ message: MESSAGES.SERIAL_IMPORT_LAYOUT_DELETED, width: 200 }, "success", 2500);
            loadLayout(null);
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            setSavingLayout(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, layoutRowId, loadLayout, newLayout]);

    const sectionTitle = { fontWeight: 600, margin: "6px 0" };

    return (
        <Popup
            visible={true}
            onHiding={closePopup}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? "Import Serial Numbers"}
            container="#root"
            width="90vw"
            height="90vh"
        >
            <input
                ref={addFilesInputRef}
                type="file"
                accept=".txt,.csv,text/plain,*/*"
                multiple
                style={{ display: "none" }}
                onChange={onAddFiles}
            />
            <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "4px 8px" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Import Tool</div>
                <TabPanel
                    showNavButtons={true}
                    deferRendering={false}
                    style={{ flex: "1 1 auto", minHeight: 0 }}
                >
                    <TabItem title="Import">
                        <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, padding: "8px 2px" }}>
                            <div style={sectionTitle}>Step 1: Select Source Files</div>
                            <Toolbar>
                                <Item location="after">
                                    <Button text="Add Files" disabled={busy} onClick={() => addFilesInputRef.current?.click()} />
                                </Item>
                                <Item location="after">
                                    <Button text="Remove File" disabled={busy} onClick={removeSelectedFile} />
                                </Item>
                                <Item location="after">
                                    <Button text="Clear List" disabled={busy} onClick={clearFileList} />
                                </Item>
                                <Item location="after">
                                    <Button text="Delete Import Data" disabled={true} />
                                </Item>
                            </Toolbar>
                            <select
                                size={5}
                                value={selectedIndex}
                                onChange={(e) => setSelectedIndex(Number(e.target.value))}
                                style={{ width: "100%", border: "1px solid #ccc", marginBottom: 10, minHeight: 96 }}
                            >
                                {files.map((f, i) => (
                                    <option key={`${f.name}-${i}`} value={i}>{f.name}</option>
                                ))}
                            </select>

                            <div style={sectionTitle}>Step 2: Verify Results and Import</div>
                            <Toolbar>
                                <Item location="after">
                                    <Button text="Preview" disabled={busy || files.length === 0} onClick={() => runImport(true)} />
                                </Item>
                                <Item location="after">
                                    <Button text="Import" type="default" disabled={busy} onClick={() => runImport(false)} />
                                </Item>
                                <Item location="after">
                                    <Button text="Cancel" disabled={!busy} onClick={cancelRun} />
                                </Item>
                                <Item location="after">
                                    <Button text="Print" disabled={busy} onClick={() => printLines(logLines, "Import Log")} />
                                </Item>
                                <Item location="after">
                                    <Button text="Print Errors" disabled={busy } onClick={() => printLines(errorLines, "Import Errors")} />
                                </Item>
                                <Item location="after">
                                    <Button text="Save" disabled={busy} onClick={saveLog} />
                                </Item>
                                <Item location="after">
                                    <Button text="Clear" disabled={busy} onClick={clearLog} />
                                </Item>
                            </Toolbar>
                            <div
                                style={{
                                    flex: "1 1 auto",
                                    overflow: "auto",
                                    border: "1px solid #ccc",
                                    background: "#fff",
                                    padding: 8,
                                    minHeight: 120,
                                    fontFamily: "Consolas, monospace",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {logLines.map((line, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            color: /^error[:\s]/i.test(line) ? "#c0392b" : /^warning[:\s]/i.test(line) ? "#b9770e" : "#333",
                                        }}
                                    >
                                        {line === "" ? " " : line}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </TabItem>
                    <TabItem title="Layout">
                        <div id="serial-layout-tab" style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto", padding: "8px 2px" }}>
                            <LoadPanel
                                visible={layoutLoading || savingLayout}
                                shading={true}
                                shadingColor="rgba(0,0,0,0.08)"
                                showIndicator={true}
                                showPane={true}
                                hideOnOutsideClick={false}
                                container="#serial-layout-tab"
                                position={{ of: "#serial-layout-tab" }}
                                message={savingLayout ? "Saving..." : "Loading..."}
                            />
                            <div style={{ marginBottom: 8 }}>
                                <Toolbar>
                                        <Item location="after">
                                            <Button text="Add" disabled={savingLayout} onClick={newLayout} />
                                        </Item>
                                    <Item location="after">
                                        <Button text="Find" disabled={savingLayout} onClick={openFind} />
                                    </Item>
                                    <Item location="after">
                                        <Button text="Save" type="default" disabled={savingLayout} onClick={saveLayout} />
                                    </Item>
                                    <Item location="after">
                                        <Button text="Delete" disabled={savingLayout || !layoutRowId} onClick={deleteLayout} />
                                    </Item>
                                </Toolbar>
                                <FormField
                                    currrentUser={currentUser}
                                    layoutId={IMPORT_LAYOUT_FORM_ID}
                                    layoutName="OMSerialNumberImportLayoutForm"
                                    defaultFormSchema={buildLayoutSchema(layoutForm)}
                                    formFields={layoutForm}
                                    setformFields={setLayoutForm}
                                    DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                                    formRefs={layoutFormRefs}
                                    onFormLoad={handleLayoutFormLoad}
                                    noSpacingBeforeFirstRow={true}
                                />
                            </div>
                            {/* Uncontrolled editable grid: the dataSource array reference is set only on load, so
                                DevExtreme edits it in place and the grid never blanks/reverts. Do NOT add
                                repaintChangesOnly or a setState on edit — either one causes the edited cell to
                                revert or the grid to reset. Save reads the edited rows from the grid instance. */}
                            <DataGrid
                                ref={fieldsGridRef}
                                dataSource={layoutFields}
                                keyExpr="FieldName"
                                showBorders={true}
                                paging={false}
                                columnAutoWidth={true}
                                rowAlternationEnabled={true}
                                height="40vh"
                                onRowUpdated={onFieldRowUpdated}
                            >
                                <Scrolling mode="standard" />
                                <GridLoadPanel enabled={true} />
                                <Editing mode="cell" allowUpdating={true} useIcons={true} />
                                <Column dataField="Name" caption="Name" allowEditing={false} />
                                <Column dataField="Start" caption="Start" dataType="number" />
                                <Column dataField="Length" caption="Length" dataType="number" />
                                <Column dataField="Required" caption="Required" dataType="boolean" />
                                <Column dataField="DefaultValue" caption="Default Value" dataType="string" />
                                <Column dataField="FieldName" visible={false} allowEditing={false} />
                            </DataGrid>
                        </div>
                    </TabItem>
                </TabPanel>
            </div>

            {showFind && (
                <Popup
                    visible={true}
                    onHiding={() => setShowFind(false)}
                    dragEnabled={true}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title="Select Layout"
                    container="#root"
                    width="80vw"
                    height="70vh"
                >
                    <DataGrid
                        ref={findGridRef}
                        dataSource={layoutList}
                        keyExpr="RowId"
                        showBorders={true}
                        columnAutoWidth={true}
                        rowAlternationEnabled={true}
                        paging={false}
                        hoverStateEnabled={true}
                        allowColumnResizing={true}
                        allowColumnReordering={true}
                        height="100%"
                        onRowClick={(e) => onFindSelect(e.data)}
                    >
                        <Scrolling mode="standard" />
                        <GridLoadPanel enabled={true} />
                        <FilterRow visible={true} />
                        <HeaderFilter visible={true} />
                        <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                            <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                        </ColumnChooser>
                        
                        <Column dataField="LayoutName" caption="Layout Name" />
                        <Column dataField="Comment" caption="Comment" />
                        <Column dataField="CompanyId" caption="Company Id" />
                        <Column dataField="DateFormat" caption="Date Format" />
                        <Column dataField="DefaultFlag" caption="Default Flag" dataType="boolean" />
                        <Column dataField="Delimiter" caption="Delimiter" />
                        <Column dataField="DelimiterChar" caption="Delimiter Char" />
                        <Column dataField="RecordDelimited" caption="Record Delimited" dataType="boolean" />
                        <Column dataField="ReservedLines" caption="Reserved Lines" dataType="number" />
                        <Column dataField="RecordNumber" caption="Record Number" dataType="number" />
                        <Column dataField="CreationUserId" caption="Created By" />
                        <Column dataField="CreationDateTime" caption="Created On" dataType="datetime" format="MM/dd/yyyy hh:mm a" />
                        <Column dataField="LastUpdateUserId" caption="Updated By" />
                        <Column dataField="LastUpdateDateTime" caption="Updated On" dataType="datetime" format="MM/dd/yyyy hh:mm a" />
                        <GridToolbar>
                            <GridToolbarItem location="after">
                                <GridLayoutManager gridKey={findGridKey} dataGrid={findGridRef} setCurrentLayout={setCurrentLayout} />
                            </GridToolbarItem>
                            <GridToolbarItem name="columnChooserButton" />
                        </GridToolbar>
                    </DataGrid>
                </Popup>
            )}
        </Popup>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SerialNumberImportPopup);
